import { captureRemixErrorBoundaryError, withSentry } from '@sentry/remix';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from '@remix-run/react';
import type { LinksFunction, LoaderFunctionArgs } from '@remix-run/node';

import './tailwind.css';
import { ReactNode } from 'react';
import { LocalApiClientProvider } from '~/modules/common/contexts/local-api-client/LocalApiClient';
import { createEnv } from '~/lib/env';
import i18next from './i18next.server';
import { useChangeLanguage } from 'remix-i18next/react';

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const env = createEnv();
  const locale = await i18next.getLocale(request);
  return {
    publicUrl: env.publicUrl,
    locale,
  };
};

export const links: LinksFunction = () => [];

export interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const ld = useLoaderData<typeof loader>();
  return (
    <html lang={ld?.locale}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function App() {
  const { publicUrl, locale } = useLoaderData<typeof loader>();
  useChangeLanguage(locale);
  return (
    <LocalApiClientProvider baseUrl={publicUrl}>
      <Outlet />
    </LocalApiClientProvider>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return <div>Something went wrong</div>;
};

export default withSentry(App);
